import { Button, Card, useToast } from '@citizen-walk/shared-ui';
import type { District, DistrictDefaultMapStyle } from '@citizen-walk/shared-types';
import { useState } from 'react';
import { doc, serverTimestamp, setDoc } from 'firebase/firestore';
import { db } from '../../lib/firebase';

/**
 * 地区別マップスタイル編集パネル (Sprint BG-DataSettings 6+7、GAP-A5)。
 *
 * 旧 v1 `District.defaultMapStyle` (4 種: gsi-std / gsi-pale / gsi-photo / osm) を踏襲。
 * user 側 MapStyle Picker の initial value に消費される (現状の MapView は OSM/PMTiles 二択だが、
 * 将来 国土地理院 タイル picker を追加した際の上位 default となる)。
 *
 * 以前は旧 DistrictDisplayConfigPanel も defaultMapStyle を編集していたが、同 panel の撤去
 * (2026-09-03) により **defaultMapStyle の編集 UI は本 panel が唯一**になった。
 */

export const STYLE_LABELS: Record<DistrictDefaultMapStyle, string> = {
  'gsi-std': '国土地理院 標準',
  'gsi-pale': '国土地理院 淡色 (推奨)',
  'gsi-photo': '国土地理院 航空写真',
  osm: 'OpenStreetMap',
};

export const DEFAULT_MAP_STYLE_KEYS: readonly DistrictDefaultMapStyle[] = [
  'gsi-std',
  'gsi-pale',
  'gsi-photo',
  'osm',
];

/** pure: District → 初期 style (未設定なら 'gsi-pale' fallback)。 */
export function draftMapStyleFromDistrict(d: District): DistrictDefaultMapStyle {
  return d.defaultMapStyle ?? 'gsi-pale';
}

/** pure: 妥当な DistrictDefaultMapStyle か判定。 */
export function isValidMapStyle(v: unknown): v is DistrictDefaultMapStyle {
  return typeof v === 'string' && (DEFAULT_MAP_STYLE_KEYS as readonly string[]).includes(v);
}

export interface DistrictMapStylePanelProps {
  district: District;
  uid: string;
  canEdit: boolean;
}

export function DistrictMapStylePanel({ district, uid, canEdit }: DistrictMapStylePanelProps) {
  const { toast } = useToast();
  const [style, setStyle] = useState<DistrictDefaultMapStyle>(() =>
    draftMapStyleFromDistrict(district),
  );
  const [saving, setSaving] = useState(false);

  async function handleSave() {
    if (!canEdit) return;
    if (!isValidMapStyle(style)) {
      toast('error', `無効なスタイル: ${style}`);
      return;
    }
    setSaving(true);
    try {
      const ref = doc(db, 'districts', district.id);
      await setDoc(
        ref,
        {
          defaultMapStyle: style,
          updatedAt: serverTimestamp(),
          updatedBy: uid,
        },
        { merge: true },
      );
      toast('success', 'マップスタイルを保存しました');
    } catch (e) {
      toast('error', `保存失敗: ${(e as Error).message}`);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card padding="lg" className="border-primary-200" data-testid="district-map-style-panel">
      <h3 className="text-base font-bold text-slate-900 mb-3">
        🗺 既定マップスタイル (Sprint BG 6+7、GAP-A5)
      </h3>
      <p className="text-xs text-slate-500 mb-3">
        user 側 MapView の初期タイル選択。未設定なら &apos;gsi-pale&apos; (淡色) fallback。 M5
        displayRules.map.defaultTileType より優先 (地区固有値が上位)。
      </p>

      <label className="grid grid-cols-1 gap-1 mb-3">
        <span className="text-xs text-slate-500">defaultMapStyle</span>
        <select
          value={style}
          onChange={(e) => setStyle(e.target.value as DistrictDefaultMapStyle)}
          disabled={!canEdit || saving}
          className="min-h-touch px-3 border border-slate-300 rounded text-sm bg-white"
          data-testid="map-style-select"
        >
          {DEFAULT_MAP_STYLE_KEYS.map((s) => (
            <option key={s} value={s}>
              {STYLE_LABELS[s]} ({s})
            </option>
          ))}
        </select>
      </label>

      <div className="flex justify-end">
        <Button
          onClick={() => void handleSave()}
          disabled={!canEdit || saving}
          data-testid="map-style-save"
        >
          {saving ? '保存中…' : '保存'}
        </Button>
      </div>
    </Card>
  );
}
